/**
 * S-23 «Агенты» и S-24 «Настройка агента». Раздел 7.4.
 *
 * ⚠️ Решение владельца 13.10.2026: агенты заводятся выключенными, ежедневные запуски
 * включает владелец здесь. Включение с ИИ — это платный вызов каждый день, поэтому
 * перед включением — подтверждение, и в нём честно сказано, платно это или нет.
 *
 * Тексты — C:/tmp/rebrand/04-voice.md, раздел 3.10. Время расчёта (02:40) не показываем:
 * человек путает его со временем, когда отчёт приходит.
 *
 * Доступность (C:/tmp/rebrand/11-a11y.md, Б3): итог переключения объявляется через постоянные
 * живые области оболочки (useAnnounce), неудача — role="alert" без автоисчезновения; строка
 * после переключения переезжает в другую секцию, поэтому фокус возвращается на её переключатель.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { fetchAgents, patchAgent, type AgentList, type AgentSettings, type AgentPatch } from '../api/client.js'
import { confirmAction, haptic } from '../telegram/call.js'
import { Icon } from '../components/icons/Icon.js'
import { NoShop, Loading, Failed, Empty, InlineError, useAnnounce, useRole, useScreenHead } from './common.js'
import { agentIcon, dayMonthOf, RUN_STATUS } from './words.js'
import artAgentsOff from '../assets/illustrations/agents-off.webp'

/** «Последний отчёт — 13 октября, готов» / «Отчётов ещё не было». */
function lastRun(a: AgentSettings): string {
  if (a.last_run === null) return 'Отчётов ещё не было'
  const date = a.last_run.at === null ? null : dayMonthOf(a.last_run.at)
  if (a.last_run.status === 'failed') {
    const next = a.enabled ? ` Следующий — завтра в ${a.deliver_at_local_time}.` : ''
    return `Отчёт${date === null ? '' : ' ' + date} не получился.${next}`
  }
  const st = RUN_STATUS[a.last_run.status] ?? 'состояние неизвестно'
  return date === null ? `Последний отчёт — ${st}` : `Последний отчёт — ${date}, ${st}`
}

const schedule = (time: string, llm: boolean): string => `Каждый день в ${time}${llm ? '' : ' · без ИИ'}`

/**
 * Переключатель. busy — идёт сохранение: повторное нажатие не уходит, но кнопка не становится
 * disabled — иначе браузер сбросил бы с неё фокус (aria-disabled оставляет фокус на месте).
 */
function Switch(
  { on, disabled, busy = false, onToggle, testid, labelledBy, describedBy }:
  { on: boolean; disabled: boolean; busy?: boolean; onToggle: () => void; testid: string; labelledBy: string; describedBy?: string },
): React.JSX.Element {
  return (
    <button type="button" role="switch" aria-checked={on} aria-labelledby={labelledBy} aria-describedby={describedBy}
      disabled={disabled} aria-disabled={busy || undefined} aria-busy={busy || undefined} data-testid={testid}
      className={'switch' + (on ? ' switch-on' : '')}
      onClick={(e) => { e.stopPropagation(); if (!busy) onToggle() }}>
      <span className="switch-knob" />
    </button>
  )
}

/**
 * Подтверждение включения — родной диалог Telegram с кнопками «OK» и «Отмена», поэтому текст —
 * вопрос, на который отвечают «OK». Платно ли это, зависит от ИИ: без ИИ отчёт бесплатный.
 */
async function confirmEnable(title: string, time: string, llm: boolean): Promise<boolean> {
  return confirmAction(`Включить ежедневный отчёт «${title}»?\n\nОн будет приходить каждый день в ${time}. ` +
    (llm ? 'Текст пишет ИИ, поэтому каждый отчёт платный.' : 'Без ИИ, по шаблону — бесплатно.') +
    ' Выключить можно в любой момент.')
}

async function confirmLlm(title: string): Promise<boolean> {
  return confirmAction(`Включить ИИ для отчёта «${title}»? Каждый отчёт станет платным. Выключить можно в любой момент.`)
}

function useAgents(): { list: AgentList | null; setList: React.Dispatch<React.SetStateAction<AgentList | null>>; fail: string | null; reload: () => void } {
  const [list, setList] = useState<AgentList | null>(null)
  const [fail, setFail] = useState<string | null>(null)
  const [n, setN] = useState(0)
  useEffect(() => {
    let alive = true
    setFail(null); setList(null)
    void fetchAgents().then((r) => { if (alive) { if (r.ok) setList(r.data); else setFail(r.error.message) } })
    return () => { alive = false }
  }, [n])
  return { list, setList, fail, reload: () => setN((x) => x + 1) }
}

// ── S-23 Агенты ─────────────────────────────────────────────────────────────
export function ScreenAgents({ onOpen }: { onOpen: (agent: string) => void }): React.JSX.Element {
  useScreenHead(undefined, 'Какие отчёты приходят и когда')
  const role = useRole()
  const announce = useAnnounce()
  const { list, setList, fail, reload } = useAgents()
  const [busy, setBusy] = useState<string | null>(null)
  // Строка переезжает между «Включены» и «Выключены» и монтируется заново: фокус надо вернуть.
  const [refocus, setRefocus] = useState<{ agent: string; n: number } | null>(null)
  const seq = useRef(0)
  const uid = useId()

  useEffect(() => {
    if (refocus === null) return
    document.querySelector<HTMLElement>(`[data-testid="agent-switch-${refocus.agent}"]`)?.focus()
  }, [refocus, list])

  if (fail !== null) return <Failed message={fail} onRetry={reload} />
  if (list === null) return <Loading label="Загрузка агентов" rows={5} />
  if (list.shop === null) return <NoShop reason={list.reason} />
  const canEdit = list.can_edit === true

  const toggle = async (a: AgentSettings): Promise<void> => {
    if (!a.enabled && !(await confirmEnable(a.title, a.deliver_at_local_time, a.llm_enabled))) return
    haptic('medium')
    announce(null)
    const flip = (on: boolean): void => {
      setList((l) => l && { ...l, items: l.items.map((x) => (x.agent === a.agent ? { ...x, enabled: on } : x)) })
      setRefocus({ agent: a.agent, n: ++seq.current })
    }
    setBusy(a.agent)
    flip(!a.enabled)                       // оптимистично
    const r = await patchAgent(a.agent, { enabled: !a.enabled })
    setBusy(null)
    if (!r.ok) {
      flip(a.enabled)
      announce({ kind: 'fail', text: `Не получилось: настройка осталась прежней. ${r.error.message}` })
      return
    }
    announce(a.enabled
      ? { kind: 'ok', text: `Отчёт «${a.title}» выключен. Он больше не придёт, пока вы его не включите.` }
      : { kind: 'ok', text: `Включено. Отчёт будет приходить в ${a.deliver_at_local_time}.` })
  }

  const row = (a: AgentSettings): React.JSX.Element => {
    const titleId = `${uid}-${a.agent}`
    const descId = `${uid}-${a.agent}-d`
    return (
      <div className="row row-split has-ic" role="listitem" key={a.agent} data-testid={'agent-' + a.agent}>
        <button type="button" className="row-hit" data-testid={'agent-open-' + a.agent} onClick={() => onOpen(a.agent)}
          aria-labelledby={titleId} aria-describedby={descId}>
          <Icon name={agentIcon(a.agent)} className="ic-muted" />
          <span className="row-main">
            <span className="row-title is-strong" id={titleId}>{a.title}</span>
            <span id={descId} className="row-main">
              <span className="row-sub">{a.about}</span>
              <span className="row-meta">{schedule(a.deliver_at_local_time, a.llm_enabled)}</span>
              <span className="row-meta">{lastRun(a)}</span>
            </span>
          </span>
        </button>
        <Switch on={a.enabled} disabled={!canEdit} busy={busy === a.agent} labelledBy={titleId}
          onToggle={() => void toggle(a)} testid={'agent-switch-' + a.agent} />
      </div>
    )
  }

  const on = list.items.filter((a) => a.enabled)
  const off = list.items.filter((a) => !a.enabled)
  return (
    <>
      {on.length === 0 && (
        <Empty art={artAgentsOff} testid="all-off" title="Агенты выключены — отчёты не приходят"
          text={role === 'owner' && canEdit
            ? 'Включите нужные: отчёт готовится ночью и приходит утром в выбранное время.'
            : 'Агентов включает владелец магазина.'} />
      )}
      {on.length > 0 && <><h2 className="sect wb-overline" id={uid + '-on'}><span>Включены</span></h2>
        <section className="card" data-testid="agents-on" role="list" aria-labelledby={uid + '-on'}>{on.map(row)}</section></>}
      {off.length > 0 && <><h2 className="sect wb-overline" id={uid + '-off'}><span>Выключены</span></h2>
        <section className="card" data-testid="agents-off" role="list" aria-labelledby={uid + '-off'}>{off.map(row)}</section></>}
      {!canEdit && <p className="hint" data-testid="owner-only">Настройки меняет владелец магазина.</p>}
    </>
  )
}

// ── S-24 Настройка агента ───────────────────────────────────────────────────
type Field = 'time' | 'chars'

/**
 * К какому полю относится ошибка сохранения: сервер присылает только текст. Если в правке одно
 * поле — оно; если оба — по словам ошибки. Ошибка не про поле (сеть, права) — ни к какому.
 */
function invalidFields(draft: AgentPatch, message: string): Field[] {
  const touched: Field[] = []
  if (draft.deliver_at_local_time !== undefined) touched.push('time')
  if (draft.max_answer_chars !== undefined) touched.push('chars')
  const byWords = touched.filter((f) => (f === 'chars' ? /Длина ответа|знак/u : /врем|\d\d:\d\d/u).test(message))
  return byWords.length > 0 ? byWords : touched.length === 1 && /Укажите|Длина|время|знак/u.test(message) ? touched : []
}

export function ScreenAgent({ agent, onAll }: { agent: string; onAll: () => void }): React.JSX.Element {
  const { list, setList, fail, reload } = useAgents()
  const announce = useAnnounce()
  const [draft, setDraft] = useState<AgentPatch>({})
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<{ text: string; fields: Field[] } | null>(null)
  const uid = useId()
  const a = list?.items.find((x) => x.agent === agent)
  useScreenHead(a?.title, a === undefined ? null : 'Настройка отчёта')

  // Ошибка поля — фокус в это поле: человек сразу исправляет, диктор читает подпись и ошибку.
  useEffect(() => {
    const f = err?.fields[0]
    if (f !== undefined) document.querySelector<HTMLElement>(`[data-testid="f-${f}"]`)?.focus()
  }, [err])

  if (fail !== null) return <Failed message={fail} onRetry={reload} />
  if (list === null) return <Loading label="Загрузка настроек агента" rows={4} />
  if (list.shop === null) return <NoShop reason={list.reason} />
  if (a === undefined) {
    return (
      <Empty icon="section-agents" title="Такого агента нет">
        <button type="button" className="btn btn-soft" data-testid="all-agents" onClick={onAll}>Все агенты</button>
      </Empty>
    )
  }
  const canEdit = list.can_edit === true && !saving

  const enabled = draft.enabled ?? a.enabled
  const llm = draft.llm_enabled ?? a.llm_enabled
  const time = draft.deliver_at_local_time ?? a.deliver_at_local_time
  const chars = draft.max_answer_chars !== undefined ? draft.max_answer_chars : a.max_answer_chars?.value ?? null
  const dirty = Object.keys(draft).length > 0

  const save = async (): Promise<void> => {
    if (draft.enabled === true && !a.enabled && !(await confirmEnable(a.title, time, llm))) return
    if (draft.llm_enabled === true && !a.llm_enabled && a.enabled && draft.enabled !== false && !(await confirmLlm(a.title))) return
    setSaving(true); setErr(null); announce(null)
    const r = await patchAgent(a.agent, draft)
    setSaving(false)
    if (!r.ok) { setErr({ text: r.error.message, fields: invalidFields(draft, r.error.message) }); return }
    haptic('light')
    setList((l) => l && { ...l, items: l.items.map((x) => (x.agent === a.agent ? r.data.item : x)) })
    setDraft({})
    announce({ kind: 'ok', text: 'Сохранено' })
  }

  const id = (k: string): string => `${uid}-${k}`
  const errId = id('err')
  // Поле: подпись — только название строки, пояснение и ошибка — через aria-describedby.
  const fieldA11y = (f: Field, hint: string): { 'aria-labelledby': string; 'aria-describedby': string; 'aria-invalid'?: true } => {
    const bad = err?.fields.includes(f) === true
    return {
      'aria-labelledby': id(f), 'aria-describedby': bad ? `${id(hint)} ${errId}` : id(hint),
      ...(bad ? { 'aria-invalid': true as const } : {}),
    }
  }
  return (
    <>
      <section className="card" data-testid="screen-body">
        <div className="row row-form">
          <span className="row-main">
            <span className="row-title" id={id('on')}>Присылать каждый день</span>
            <span className="row-meta" id={id('on-d')}>{lastRun(a)}</span>
          </span>
          <Switch on={enabled} disabled={!canEdit} labelledBy={id('on')} describedBy={id('on-d')} testid="f-enabled"
            onToggle={() => setDraft({ ...draft, enabled: !enabled })} />
        </div>
        <div className="row row-form">
          <span className="row-main">
            <span className="row-title" id={id('llm')}>Текст пишет ИИ</span>
            <span className="row-sub" id={id('llm-d')}>{llm ? 'Платно. Без ИИ — те же цифры по шаблону, бесплатно.' : 'Сейчас без ИИ: цифры по шаблону, бесплатно.'}</span>
          </span>
          <Switch on={llm} disabled={!canEdit} labelledBy={id('llm')} describedBy={id('llm-d')} testid="f-llm"
            onToggle={() => setDraft({ ...draft, llm_enabled: !llm })} />
        </div>
        <div className="row row-form">
          <span className="row-main">
            <label className="row-title" id={id('time')} htmlFor={id('time-i')}>Когда присылать</label>
            <span className="row-sub" id={id('time-d')}>По времени магазина. Отчёт готовится ночью.</span>
          </span>
          <input className="inp" type="time" id={id('time-i')} value={time} disabled={!canEdit} data-testid="f-time"
            {...fieldA11y('time', 'time-d')}
            onChange={(e) => setDraft({ ...draft, deliver_at_local_time: e.target.value })} />
        </div>
        {a.max_answer_chars !== undefined && (
          <div className="row row-form">
            <span className="row-main">
              <label className="row-title" id={id('chars')} htmlFor={id('chars-i')}>Длина ответа покупателю</label>
              <span className="row-sub" id={id('chars-d')}>
                Не больше стольких знаков. Wildberries принимает от 2 до 5000.
                {a.max_answer_chars.is_default && draft.max_answer_chars === undefined && <span className="row-meta">Сейчас — по умолчанию</span>}
              </span>
            </span>
            <input className="inp" type="number" id={id('chars-i')} inputMode="numeric" min={2} max={5000} disabled={!canEdit} data-testid="f-chars"
              {...fieldA11y('chars', 'chars-d')}
              value={chars ?? ''} onChange={(e) => setDraft({ ...draft, max_answer_chars: e.target.value === '' ? null : Number(e.target.value) })} />
          </div>
        )}
      </section>
      {err !== null && <InlineError text={err.text} testid="save-msg" id={errId} />}
      {list.can_edit === true
        ? <div className="form-actions">
            <button type="button" className="btn btn-block" data-testid="save" disabled={!dirty || saving} onClick={() => void save()}>
              {saving ? <><Icon name="action-busy" className="spin" />Сохранение…</> : 'Сохранить'}
            </button>
          </div>
        : <p className="hint" data-testid="owner-only">Настройки меняет владелец магазина.</p>}
    </>
  )
}
